import http from "./http";

export type PartnerPaymentRequestStatus = "PENDING" | "REQUESTED" | "IN_TRANSIT" | "PAID" | "REJECTED";

export interface AdminPartnerPaymentRequestRow {
	id: number;
	partnerId: number;
	station: {
		id: number | null;
		name: string | null;
		displayName?: string | null;
		destinationId?: number | null;
		cityName?: string | null;
		countryName?: string | null;
		currencyCode?: string | null;
	};
	requestTime: string;
	requestAmount: number;
	requestType?: "UNDER_MIN_PAYOUT" | "FROM_MIN_PAYOUT" | null;
	status: PartnerPaymentRequestStatus | string;
	paidAmount: number | null;
	paidTime: string | null;
	orderCount?: number;
	/** Full bookings for this payout request when loaded from grouped cycle details API. */
	orders?: GroupedOrderDetailRow[];
}

// Backward-compatible aliases used by the admin page
export type AdminPaymentRequest = AdminPartnerPaymentRequestRow;

export async function getAdminPartnerPaymentRequests(status?: string) {
	const params: any = {};
	if (status) params.status = status;

	const resp = await http.get(`/admin/partner-payment-requests`, { params });
	return (resp.data?.data || []) as AdminPartnerPaymentRequestRow[];
}

export async function adminPayPartnerPaymentRequest(id: number, paidAmount: number) {
	const resp = await http.post(`/admin/partner-payment-requests/${id}/pay`, {
		paid_amount: paidAmount,
	});
	return resp.data;
}

/** Pay qua Wise (maker-checker 01/09): máy tạo lệnh trên Wise, Quân duyệt trong app Wise — KHÔNG đụng tiền ở bước này. */
export async function adminCreateWiseTransfer(id: number, body: { confirmMismatch?: boolean; resend?: boolean } = {}) {
	/* confirmMismatch = admin đã đối chiếu số TK hồ sơ ≠ Wise với điểm ("Đã kiểm, cho đi" — Quân 11/09). */
	const resp = await http.post(`/admin/partner-payment-requests/${id}/wise-transfer`, body);
	return resp.data;
}

/** 17/10/2026: yêu cầu ĐÃ PAID mà lệnh Wise bị ngân hàng trả lại (tiền đã về ví) — Admin gửi lại; cổng trạm không đổi. */
export interface WiseReturnedInfo {
	paymentRequestId: number;
	partnerId: number;
	wiseTransferId: number;
	targetAmount: number;
	targetCurrency: string;
	returnedAt?: string;
	/* Lệnh gửi lại mới nhất (AWAITING_APPROVE · SENT · PAID) — null là chưa gửi lại. */
	repayStatus: string | null;
}

export async function adminGetWiseReturned(): Promise<{ success: boolean; returned: WiseReturnedInfo[] }> {
	const resp = await http.get(`/admin/partner-payment-requests/wise-returned`);
	return resp.data;
}

/** Một lệnh Wise của request — status nội bộ: AWAITING_APPROVE · SENT · PAID · CANCELLED · FAILED. */
export interface WiseTransferInfo {
	id: number;
	wiseTransferId: number;
	mode: string;
	status: string;
	wiseStatus?: string | null;
	sourceAmount: number;
	targetAmount: number;
	targetCurrency: string;
	wiseFee?: number | null;
	feeBorneBy?: string | null;
	createdAt?: string;
	updatedAt?: string;
	paidMarkedAt?: string | null;
}

export async function adminGetWiseTransferStatus(id: number): Promise<{ success: boolean; transfers: WiseTransferInfo[] }> {
	const resp = await http.get(`/admin/partner-payment-requests/${id}/wise-transfer`);
	return resp.data;
}

/** Hỏi Wise NGAY cho lệnh đang mở của request (không chờ job 15') — trả lại danh sách lệnh mới nhất. */
export async function adminRefreshWiseTransfer(id: number): Promise<{ success: boolean; transfers: WiseTransferInfo[] }> {
	const resp = await http.post(`/admin/partner-payment-requests/${id}/wise-transfer/refresh`, {});
	return resp.data;
}

export async function adminRepayPartnerPaymentRequest(id: number, repayAmount: number) {
	const resp = await http.post(`/admin/partner-payment-requests/${id}/repay`, {
		repay_amount: repayAmount,
	});
	return resp.data;
}

export async function adminBulkSendFinalizedEmail(requestIds: number[]) {
	const resp = await http.post(`/admin/partner-payment-requests/bulk-send-email`, {
		requestIds,
	});
	return resp.data;
}

export async function getPartnerPaymentRequests(status?: string) {
	return getAdminPartnerPaymentRequests(status);
}

export async function payPartnerPaymentRequest(id: number, paidAmount: number) {
	return adminPayPartnerPaymentRequest(id, paidAmount);
}

export async function repayPartnerPaymentRequest(id: number, repayAmount: number) {
	return adminRepayPartnerPaymentRequest(id, repayAmount);
}

export type GroupedTypeAggregate = "UNDER_MIN_PAYOUT" | "FROM_MIN_PAYOUT";

export interface GroupedStationRequestRow {
	partnerId: number;
	stationName: string;
	vnYear: number;
	vnMonth: number;
	period: 1 | 2;
	cycleLabel: string;
	requestCount: number;
	typeAggregate: GroupedTypeAggregate | string;
	totalAmount: number;
	pendingAmount: number;
	pendingRequestCount: number;
	currencyCode: string | null;
	totalBookings: number;
	status: string;
	requestIds: number[];
	station?: {
		id: number;
		name: string | null;
		displayName: string | null;
		destinationId: string | null;
		currencyCode: string | null;
		type?: number | null;
	} | null;
}

export interface GroupedStationRequestListResponse {
	items: GroupedStationRequestRow[];
	total: number;
	page: number;
	pageSize: number;
	cycleConfig?: { cycle1EndDay: number };
}

export async function fetchGroupedPartnerPaymentRequests(params: {
	page?: number;
	pageSize?: number;
	search?: string;
	statusAggregate?: "IN_TRANSIT" | "PAID" | "all";
	period?: 1 | 2 | "all";
	vnYear?: number;
	vnMonth?: number;
	destinationId?: string;
}) {
	const query: Record<string, string | number | undefined> = {};
	if (params.page != null) query.page = params.page;
	if (params.pageSize != null) query.pageSize = params.pageSize;
	if (params.search) query.search = params.search;
	if (params.statusAggregate && params.statusAggregate !== "all") query.statusAggregate = params.statusAggregate;
	if (params.period != null && params.period !== "all") query.period = params.period;
	if (params.vnYear != null) query.vnYear = params.vnYear;
	if (params.vnMonth != null) query.vnMonth = params.vnMonth;
	if (params.destinationId != null && `${params.destinationId}`.trim() !== "") {
		query.destinationId = params.destinationId;
	}

	const resp = await http.get(`/admin/partner-payment-requests/grouped`, { params: query });
	return resp.data as GroupedStationRequestListResponse;
}

export interface GroupedOrderDetailRow {
	id: number;
	status: number;
	refundStatus: number | null;
	customerName: string | null;
	quantity: number | null;
	totalMoney: number | null;
	timeIn: string | null;
	timeOut: string | null;
}

export interface GroupedStationRequestDetailItem {
	id: number;
	partnerId: number;
	requestTime: string;
	requestAmount: number;
	requestType?: "UNDER_MIN_PAYOUT" | "FROM_MIN_PAYOUT" | null;
	status: string;
	paidAmount: number | null;
	paidTime: string | null;
	/** Snapshot on the payment request (audit). */
	effectiveStationCom: number | null;
	payoutBasisUnits: number | null;
	pricingCurrencyCode: string | null;
	orderCount: number;
	orderIds: number[];
	/** Full booking list for this request (admin reconciliation). */
	orders?: GroupedOrderDetailRow[];
	ordersPreview: Array<{
		id: number;
		status: number;
		customerName: string | null;
	}>;
}

export interface GroupedBookingLineRow {
	paymentRequestId: number;
	orderId: number;
	customerName: string | null;
	quantity: number | null;
	totalMoney: number | null;
	timeIn: string | null;
	timeOut: string | null;
	orderStatus: number;
	refundStatus: number | null;
	requestAmount: number;
	effectiveStationCom: number | null;
	payoutBasisUnits: number | null;
}

export interface GroupedStationRequestDetailsResponse {
	partnerId: number;
	vnYear: number;
	vnMonth: number;
	period: 1 | 2;
	cycleLabel: string;
	stationName: string;
	/** Partner local currency when set. */
	currencyCode?: string | null;
	/** Station ledger adjustment bucket (partner-level, for context when checking payout). */
	availableDeductionAmount?: number | null;
	items: GroupedStationRequestDetailItem[];
	/** Denormalized lines: every order × parent request totals for spreadsheet-style review. */
	bookingLines?: GroupedBookingLineRow[];
	cycleBounds: { fromUtc: string; toUtcExclusive: string };
}

export async function fetchGroupedPartnerPaymentRequestDetails(params: {
	partnerId: number;
	vnYear: number;
	vnMonth: number;
	period: 1 | 2;
}) {
	const resp = await http.get(`/admin/partner-payment-requests/grouped/details`, {
		params: {
			partnerId: params.partnerId,
			vnYear: params.vnYear,
			vnMonth: params.vnMonth,
			period: params.period,
		},
	});
	return resp.data as GroupedStationRequestDetailsResponse;
}

/** Reject a single payout request with a required reason (backend: POST /:id/reject). */
export async function adminRejectPartnerPaymentRequest(id: number, reason: string) {
	const resp = await http.post(`/admin/partner-payment-requests/${id}/reject`, { reason });
	return resp.data;
}

/**
 * Approve all PENDING requests in a station group in ONE call.
 * Backend contract: POST /admin/partner-payment-requests/approve { requestIds } (bulk, transactional).
 */
export async function adminApproveGroupedPartnerRequests(ids: number[]) {
	const resp = await http.post(`/admin/partner-payment-requests/approve`, { requestIds: ids });
	return resp.data;
}

/** Reject every request in a group with the same reason (backend reject is per-id). */
export async function adminRejectGroupedPartnerRequests(ids: number[], reason: string) {
	await Promise.all(ids.map((id) => adminRejectPartnerPaymentRequest(id, reason)));
}

export async function adminBulkPayGroupedPartnerRequests(payload: {
	partnerId: number;
	vnYear: number;
	vnMonth: number;
	period: 1 | 2;
	/** Total payment amount for the group (allocated across IN_TRANSIT requests proportionally). */
	paidAmount: number;
	note?: string;
}) {
	const resp = await http.post(`/admin/partner-payment-requests/grouped/pay`, payload);
	return resp.data as {
		paidCount: number;
		paidRequestIds: number[];
		message?: string;
	};
}
